import {Capacitor, registerPlugin} from '@capacitor/core';
import {
  APPLE_SUBSCRIPTION_PRODUCT_IDS,
  isAppleSubscriptionProduct
} from './appleProductCatalog.js';

const AppleStoreKitPlugin = registerPlugin('SilaTrailStoreKit');
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function requireNativeIos({
  platform = Capacitor.getPlatform(),
  native = Capacitor.isNativePlatform()
} = {}) {
  if (platform !== 'ios' || !native) {
    throw new Error('Apple purchases are available only in the native iOS application.');
  }
}

function requireApprovedProduct(productId) {
  if (!isAppleSubscriptionProduct(productId)) {
    throw new Error('The selected Apple subscription product is not approved.');
  }
}

function requireAppAccountToken(appAccountToken) {
  if (!UUID_PATTERN.test(appAccountToken || '')) {
    throw new Error('A valid Apple purchase account token is required.');
  }
}

export async function getAppleProducts(dependencies = {}) {
  requireNativeIos(dependencies);
  const plugin = dependencies.plugin || AppleStoreKitPlugin;
  return plugin.getProducts({productIds: APPLE_SUBSCRIPTION_PRODUCT_IDS});
}

export async function purchaseAppleProduct({productId, appAccountToken}, dependencies = {}) {
  requireNativeIos(dependencies);
  requireApprovedProduct(productId);
  requireAppAccountToken(appAccountToken);
  const plugin = dependencies.plugin || AppleStoreKitPlugin;
  return plugin.purchase({productId, appAccountToken});
}

export async function getAppleCurrentEntitlements(dependencies = {}) {
  requireNativeIos(dependencies);
  const plugin = dependencies.plugin || AppleStoreKitPlugin;
  return plugin.currentEntitlements();
}

export async function syncApplePurchases(dependencies = {}) {
  requireNativeIos(dependencies);
  const plugin = dependencies.plugin || AppleStoreKitPlugin;
  return plugin.sync();
}

export async function manageAppleSubscriptions(dependencies = {}) {
  requireNativeIos(dependencies);
  const plugin = dependencies.plugin || AppleStoreKitPlugin;
  return plugin.manageSubscriptions();
}

export async function finishAppleTransaction(transactionId, dependencies = {}) {
  requireNativeIos(dependencies);
  if (!/^\d+$/.test(String(transactionId || ''))) {
    throw new Error('A valid Apple transaction identifier is required.');
  }
  const plugin = dependencies.plugin || AppleStoreKitPlugin;
  return plugin.finishTransaction({transactionId: String(transactionId)});
}

export function addAppleTransactionListener(listener, dependencies = {}) {
  requireNativeIos(dependencies);
  if (typeof listener !== 'function') throw new Error('A transaction listener is required.');
  const plugin = dependencies.plugin || AppleStoreKitPlugin;
  return plugin.addListener('transactionUpdated', listener);
}
